import { MouseEvent, useEffect, useMemo, useRef } from 'react';

import { insertProductImpressionTrackEvent } from '@comeon/analytics/payments';
import { TranslationHash } from '~/components/translation/use-translations';
import LazyLoadImage from '~/components/utilities/lazyload-image';
import useCasinoLocation from '~/hooks/use-casino-location';
import GameNameInfiniteScrolling from './game-name';
import useLaunchNewGame from './use-launch-new-game';
import { getForegroundImageSrcSet, WidgetData } from './utils';
import {
	AvailableNowTitle,
	BackGroundImage,
	CardWrapperAvailable,
	ForegroundImage,
	MiddleWrapper,
	PlayGameButton,
	TitleWrapper
} from './widget-styles';
import useConfiguration from '~/configuration/use-configuration';

type Props = {
	translations: TranslationHash;
	exploreGameItem: WidgetData['exploreGameItems'][0];
} & WidgetData;

const HorizontalCardAvailable = ({
	backgroundImage,
	foregroundImage,
	translations,
	availableNowText,
	exploreGameItem,
	listName,
	trackGameClick,
	type
}: Props) => {
	const configuration = useConfiguration();

	const foregroundImageSrcSet = useMemo(() => getForegroundImageSrcSet(foregroundImage, 128), [foregroundImage]);

	const containerRef = useRef<HTMLDivElement>(null);

	const launchGame = useLaunchNewGame({ type, trackGameClick });
	const { isLiveCasinoLocation } = useCasinoLocation();

	const handlePlayGameClick = (e: MouseEvent<HTMLElement, globalThis.MouseEvent>) => {
		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		launchGame(e, exploreGameItem.code);
	};

	useEffect(() => {
		insertProductImpressionTrackEvent({
			impressions: [
				{
					name: exploreGameItem.name,
					id: exploreGameItem.id,
					category: type,
					list: `${isLiveCasinoLocation ? 'LiveCasino' : 'Casino'} ${listName}`,
					position: 1
				}
			]
		});
	}, [exploreGameItem.id, exploreGameItem.name, isLiveCasinoLocation, listName, type]);

	return (
		<BackGroundImage
			backgroundImage={backgroundImage}
			onClick={handlePlayGameClick}
			tmpGameIconSrc={configuration?.casino?.tmpGameIcon?.src}
		>
			<CardWrapperAvailable>
				<ForegroundImage>
					<LazyLoadImage
						src={foregroundImage}
						srcSet={foregroundImageSrcSet}
						sizes="128px"
						alt={`${exploreGameItem.name} image`}
						lazyload
					/>
				</ForegroundImage>

				<MiddleWrapper ref={containerRef}>
					<GameNameInfiniteScrolling gameName={exploreGameItem.name} containerRef={containerRef} />
					<TitleWrapper>
						<AvailableNowTitle>{availableNowText}</AvailableNowTitle>
					</TitleWrapper>
					<PlayGameButton>{translations.WIDGET_PLAY_NOW}</PlayGameButton>
				</MiddleWrapper>
			</CardWrapperAvailable>
		</BackGroundImage>
	);
};

export default HorizontalCardAvailable;
